import type { Menu as PrimeMenu } from "primereact/menu";
import type { MenuItem } from "primereact/menuitem";
import { classNames } from "primereact/utils";
import { Children, isValidElement, useCallback, useId, useMemo, useRef, useState } from "react";
import type { MouseEvent, ReactElement, ReactNode, SyntheticEvent } from "react";

import { useEscFechaSoMenu } from "../../hooks/use-esc-fecha-so-menu";
import { callAll } from "../../utils/call-all";
import { useStableRest } from "../../utils/use-stable-rest";
import { isPrimeIconClass, primeIcon } from "../icon";

import { TABLE_ACTIONS_LABEL } from "./messages";
import type {
  ItemPart,
  ItemProps,
  TableActionsMenuRootAttributes,
  TableActionsMenuViewModel,
  UseTableActionsMenuOptions,
} from "./types-table-actions-menu";

const isItemPart = (type: unknown): type is ItemPart =>
  typeof type === "function" && "toItem" in type;

/**
 * Separa o filho `Trigger` (fora do painel) dos itens (viram o `model` do `Menu`), pelos marcadores `isTrigger` e `toItem`
 * dos componentes. O `Trigger` precisa ser filho direto do `Root` (não dentro de um fragmento ou wrapper); sem ele,
 * lança um erro claro. Qualquer outro filho é ignorado.
 */
function partitionMenuChildren(children: ReactNode): {
  trigger: ReactElement;
  items: ReactElement[];
} {
  let trigger: ReactElement | null = null;
  const items: ReactElement[] = [];
  for (const child of Children.toArray(children)) {
    if (!isValidElement(child)) continue;
    if ((child.type as { isTrigger?: boolean }).isTrigger) trigger = child;
    else if (isItemPart(child.type)) items.push(child);
  }
  if (!trigger) {
    throw new Error("TableActionsMenu.Root exige um TableActionsMenu.Trigger como filho direto");
  }
  return { trigger, items };
}

/** Item do `model` do `Menu` a partir das props de um `Item`: ícone do DS (14), comando e atributos do `<li>` em `data`. */
function toMenuItem(
  { label, icon, onClick, disabled = false, testId, className, style, ...rest }: ItemProps,
  itemClassName: string | undefined,
): MenuItem {
  return {
    label,
    icon: icon && (isPrimeIconClass(icon) ? icon : primeIcon(icon, 14)),
    disabled,
    command: () => onClick(),
    className: classNames(itemClassName, className),
    style,
    data: { "data-testid": testId, ...rest },
  };
}

/**
 * `pt` do `Menu`: o `id` e o `aria-labelledby` da lista (`role="menu"`, alvo do `aria-controls`) e os atributos extras
 * de cada item (`data`), que vão para o `<li>`.
 */
const menuPt = (menuId: string, triggerId: string) => ({
  menu: { id: menuId, "aria-labelledby": triggerId },
  menuitem: ({ context }: { context: { item: MenuItem } }) => context.item.data as object,
});

/**
 * ViewModel do TableActionsMenu: separa o `Trigger` dos itens, ref do `Menu` popup, ids de `aria-controls`, o `model` dos itens, o estado aberto e a
 * volta do foco ao botão ao fechar. Setas, Home/End, Enter/Espaço, Esc e Tab são do `Menu` do Prime; sem JSX.
 */
export function useTableActionsMenu(
  {
    testId,
    "aria-label": ariaLabel = TABLE_ACTIONS_LABEL,
    className,
    style,
    classNames: parts,
    panelStyle,
    onKeyDown: onKeyDownDaTela,
    onKeyDownCapture: onKeyDownCaptureDaTela,
    ...props
  }: UseTableActionsMenuOptions & TableActionsMenuRootAttributes,
  children: ReactNode,
): TableActionsMenuViewModel {
  const { trigger, items } = partitionMenuChildren(children);
  const rest = useStableRest(props);
  const menuRef = useRef<PrimeMenu>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const esc = useEscFechaSoMenu();
  const [open, setOpen] = useState(false);
  const baseId = useId();

  const partTestId = useCallback(
    (part: string) => (testId ? `${testId}-${part}` : undefined),
    [testId],
  );

  const toggle = useCallback((event: MouseEvent<HTMLElement>) => {
    menuRef.current?.toggle(event);
  }, []);

  const { aberto, onShow: marcarAberto, onHide: marcarFechado } = esc;

  const onShow = useCallback(() => {
    marcarAberto();
    setOpen(true);
  }, [marcarAberto]);

  const onHide = useCallback(
    (_event: SyntheticEvent) => {
      if (!aberto.current) return;
      marcarFechado();
      setOpen(false);
      triggerRef.current?.focus();
    },
    [aberto, marcarFechado],
  );

  // O Esc que a lista já consumiu não pode chegar ao listener global do Prime, que fecharia o `Dialog` em volta.
  const { onKeyDownCapture, onKeyDown } = esc;

  const model = items.map((item) =>
    toMenuItem(
      (item.type as unknown as ItemPart).toItem(item.props as never, partTestId),
      parts?.item,
    ),
  );

  const triggerId = `${baseId}-trigger`;
  const menuId = `${baseId}-menu`;
  const context = useMemo(
    () => ({ triggerRef, triggerId, menuId, ariaLabel, open, toggle, partTestId }),
    [triggerId, menuId, ariaLabel, open, toggle, partTestId],
  );

  return {
    trigger,
    menuRef,
    model,
    menuPt: menuPt(menuId, triggerId),
    onShow,
    onHide,
    onKeyDownCapture: callAll(onKeyDownCapture, onKeyDownCaptureDaTela),
    onKeyDown: callAll(onKeyDown, onKeyDownDaTela),
    testId,
    className,
    style,
    classNames: parts ?? {},
    panelStyle,
    rest,
    context,
  };
}
